// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import qs from 'query-string';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ChannelType } from '@/models/channel';
import { Switch } from '@/components/ui/switch';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@heroui/react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { type ModalProps } from '@/stores/use-modal-store.ts';
import React, { useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { API_BASE } from '@/config';
import { useServerStore } from '@/stores/use-server-store.ts';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';

const formSchema = z.object({
  name: z
    .string()
    .min(1, {
      message: 'Channel name is required.',
    })
    .refine((name) => name !== 'general', {
      message: "Channel name cannot be 'general'",
    }),
  type: z.nativeEnum(ChannelType),
  isPrivate: z.boolean(),
  maxParticipants: z
    .number()
    .min(1, { message: 'Must be at least 1 participant.' })
});

export const CreateChannelModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();
  const { serverId } = useParams<{
    serverId: string;
  }>();
  const { channelType } = data;
  const { addChannel } = useServerStore();
  const socket = useSocket();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      type: channelType || ChannelType.TEXT,
      isPrivate: false,
      maxParticipants: 50,
    },
  });

  useEffect(() => {
    if (channelType) {
      form.setValue('type', channelType);
    } else {
      form.setValue('type', ChannelType.TEXT);
    }
  }, [channelType, form]);

  const isLoading = form.formState.isSubmitting;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const url = qs.stringifyUrl({
        url: `${API_BASE}/api/channels`,
        query: {
          serverId: serverId,
        },
      });
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(values),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const newChannel = await response.json();

      addChannel(newChannel);

      if (socket?.readyState !== WebSocket.OPEN) {
        return;
      }
      socket.send(
        JSON.stringify({
          type: 'channel-add',
          payload: newChannel,
        })
      );

      form.reset();
      onClose();
    } catch {
      toast.error('Something went wrong');
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              {t('CreateChannelModal.title')}
            </ModalHeader>
            <ModalBody></ModalBody>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
              >
                <div className="space-y-2 px-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-bold uppercase">
                          {t('CreateChannelModal.channelName')}
                        </FormLabel>

                        <FormControl>
                          <Input
                            disabled={isLoading}
                            placeholder={t(
                              'CreateChannelModal.channelNamePlaceholder'
                            )}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('CreateChannelModal.channelType')}
                        </FormLabel>
                        <select
                          disabled={isLoading}
                          value={field.value}
                          onChange={field.onChange}
                          className="mt-1 block w-full rounded-md border-1 border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          {Object.values(ChannelType).map((type) => (
                            <option key={type} value={type}>
                              {type.toLocaleUpperCase()}
                            </option>
                          ))}
                        </select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="maxParticipants"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('EditChannelModal.maxParticipants')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            disabled={isLoading}
                            min={1}
                            max={50}
                            placeholder={t(
                              'EditChannelModal.maxParticipantsPlaceholder'
                            )}
                            {...field}
                            onChange={(e) => field.onChange(e.target.valueAsNumber)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="isPrivate"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-start gap-2">
                        <FormLabel className="text-xs font-bold uppercase">
                          {t('CreateChannelModal.private')}
                        </FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <ModalFooter className="px-6 py-4">
                  <Button
                    color="primary"
                    disabled={isLoading}
                    type="submit"
                    variant="primary"
                  >
                    {t('CreateChannelModal.create')}
                  </Button>
                </ModalFooter>
              </form>
            </Form>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
